// Remembers where a logged-out visitor was heading so we can send them
// back there after they sign in (#571). Backed by sessionStorage so it
// survives the full page reload of a cold SPA start (a shared deep link
// opened in a fresh tab), yet is naturally scoped to the tab and cleared
// when it closes.
//
// Two safety properties matter:
//   * single-use  — consuming clears it, so a stale destination can never
//     hijack a later, unrelated login.
//   * validated   — only same-origin absolute paths are ever stored or
//     returned. Anything that could drive an open redirect (a scheme, a
//     host, a protocol-relative "//host") is rejected. This is the guard
//     that keeps a crafted `?return=` style value from bouncing a freshly
//     authenticated member off to an attacker's site.

const STORAGE_KEY = "ff-return-path";

// A value we are willing to navigate to after login. It must be a path on
// our own origin: exactly one leading slash, then a normal path character.
// Rejects "" , "https://evil", "//evil", "javascript:...", "/\\evil".
// Exported for reuse wherever a server/user-supplied href must be confined
// to our own origin (e.g. the campaign landing CTA — see Campaign.tsx).
export const isSafeInternalPath = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  if (value.length < 1) return false;
  if (value.charAt(0) !== "/") return false;
  // "//evil.example" is protocol-relative — the browser treats it as a
  // host, so it must not slip through the single-leading-slash check.
  if (value.charAt(1) === "/") return false;
  // "/\evil.example" — some browsers normalise backslashes to slashes,
  // making this another protocol-relative smuggle.
  if (value.charAt(1) === "\\") return false;
  return true;
};

export const storeReturnPath = (path: string): void => {
  if (!isSafeInternalPath(path)) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, path);
  } catch (e) {
    // sessionStorage can throw (private mode / storage disabled). A lost
    // return path just means the default post-login landing — never fatal.
  }
};

export const consumeReturnPath = (): string | null => {
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null) window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
  return isSafeInternalPath(stored) ? stored : null;
};
